import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Link, Stack, Tooltip, Typography } from '@mui/material';
import type { GridInitialState } from '@mui/x-data-grid-premium';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { addMonths, differenceInCalendarMonths, format, parseISO, startOfMonth } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

import { CallbackMonthModal } from '~/components/callbacks/CallbackMonthModal';
import type { CallbackTrend } from '~/components/callbacks/callbackTrend';
import {
  countLabel,
  overAllowanceFrom,
  overTargetLabel,
  requirementLabel,
  requirementLines,
  trendBetween,
  trendColor,
} from '~/components/callbacks/callbackTrend';
import {
  MONTH_CELL_CLASS,
  MONTH_CELL_SX,
  MonthCellButton,
} from '~/components/serviceHistory/MonthCellButton';
import { LiftAITable } from '~/components/table';
import type { LiftAIGridColDef } from '~/components/table/colDefTemplate';
import type { CallbackCarSummary, CallbackWindow } from '~/data/schemas/callbacks';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { buildUrlForElevator } from '~/utils/urlBuilder';

// The month width the maintenance grid uses. With the narrower Requirement and Elevator
// columns below it leaves the totals on screen from 1440px up. A scope with a Property
// column, or a narrower window, still scrolls.
const MONTH_COL_WIDTH = 52;

const initialState: GridInitialState = {
  sorting: { sortModel: [{ field: 'callbacks', sort: 'desc' }] },
};

// Matched on the month itself rather than on position, so a row that does not cover
// the whole axis still draws each month under its own column.
const cellFor = (row: CallbackCarSummary, month: string | undefined) =>
  month === undefined ? undefined : row.months.find((cell) => cell.monthStart === month);

const overAllowanceByMonth = (row: CallbackCarSummary): Record<string, boolean> => {
  const flags = overAllowanceFrom(
    row.months.map((cell) => cell.callbacks),
    row.months.map((cell) => cell.emergency),
    row.targetRate,
    row.emergencyTargetRate,
  );
  return Object.fromEntries(row.months.map((cell, index) => [cell.monthStart, flags[index]]));
};

const TREND_WORDS: Record<CallbackTrend, string> = {
  higher: 'more than the month before',
  lower: 'fewer than the month before',
  same: 'unchanged',
};

/** The arrow beside a count, or nothing when there is no movement to show. */
function TrendArrow({ trend }: { trend: CallbackTrend }) {
  if (trend === 'same') return null;
  const Icon = trend === 'higher' ? ArrowUpwardIcon : ArrowDownwardIcon;
  return <Icon sx={{ fontSize: 13 }} />;
}

/** Marks an elevator that has broken one of its two contracted rates, and says which. */
function OverTargetMark({ row }: { row: CallbackCarSummary }) {
  const label = overTargetLabel(row.carName, row.overTarget, row.overEmergencyTarget);
  if (!label) return null;
  return (
    <Tooltip
      title={`${row.annualizedRate} callbacks and ${row.emergencyAnnualizedRate} emergency a year, against ${requirementLabel(row.targetRate, row.emergencyTargetRate)}`}
    >
      <WarningAmberIcon titleAccess={label} sx={{ fontSize: 16, color: 'error.main' }} />
    </Tooltip>
  );
}

export function CallbacksTable({
  summaries,
  loading,
  window,
  alwaysShowProperty = false,
}: {
  summaries: readonly CallbackCarSummary[];
  loading: boolean;
  window: CallbackWindow | null;
  alwaysShowProperty?: boolean;
}) {
  const apiRef = useGridApiRef();
  const tableHeight = useDetailTableHeight();
  const [open, setOpen] = useState<{
    carId: string;
    carName: string;
    monthStart: string;
  } | null>(null);

  // Scopes that span buildings need the property to tell two "Car 1" rows apart.
  const qualify = useMemo(
    () => alwaysShowProperty || new Set(summaries.map((row) => row.propertyId)).size > 1,
    [alwaysShowProperty, summaries],
  );

  const months = useMemo(() => {
    if (!window || summaries.length === 0) return [];
    const first = startOfMonth(parseISO(window.startDate));
    const last = startOfMonth(parseISO(window.endDate));
    const count = differenceInCalendarMonths(last, first) + 1;
    return Array.from({ length: count }, (_, i) => format(addMonths(first, i), 'yyyy-MM-01'));
  }, [window, summaries.length]);

  const columns = useMemo<LiftAIGridColDef<CallbackCarSummary>[]>(() => {
    const monthColumns = months.map<LiftAIGridColDef<CallbackCarSummary>>((month, index) => ({
      field: `month_${month}`,
      headerName: format(parseISO(month), 'MMM'),
      width: MONTH_COL_WIDTH,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      disableExport: true,
      valueGetter: (_value, row) => cellFor(row, month)?.callbacks ?? 0,
      cellClassName: MONTH_CELL_CLASS,
      renderCell: ({ row }) => {
        const cell = cellFor(row, month);
        if (!cell) return null;
        const trend = trendBetween(cell.callbacks, cellFor(row, months[index - 1])?.callbacks);
        const overAllowance = overAllowanceByMonth(row)[month] ?? false;
        const monthName = format(parseISO(month), 'MMMM yyyy');
        const emergency = cell.emergency
          ? `, ${cell.emergency} of them ${cell.emergency === 1 ? 'an entrapment' : 'entrapments'}`
          : '';

        return (
          <MonthCellButton
            title={`${row.carName} \u00b7 ${monthName} \u2014 ${countLabel(cell.callbacks)}${emergency} \u00b7 ${TREND_WORDS[trend]}${
              overAllowance ? ' \u00b7 past the year\u2019s allowance' : ''
            }`}
            label={`${row.carName}, ${monthName}, ${countLabel(cell.callbacks)}`}
            onClick={() =>
              setOpen({ carId: row.carId, carName: row.carName, monthStart: cell.monthStart })
            }
            color={trendColor(trend)}
            dataAttributes={{ 'data-trend': trend, 'data-over-allowance': String(overAllowance) }}
          >
            {cell.callbacks}
            <TrendArrow trend={trend} />
          </MonthCellButton>
        );
      },
    }));

    return [
      {
        field: 'carName',
        headerName: 'Elevator',
        // Safe only because the tab keeps this table unmounted while hidden: flex
        // resolved against a zero-width container collapses and is never recomputed.
        flex: 1,
        minWidth: 160,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" gap={0.75} height="100%">
            <Link
              href={buildUrlForElevator(row.carId)}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              underline="hover"
              onClick={(event) => event.stopPropagation()}
            >
              {row.carName}
            </Link>
            <OverTargetMark row={row} />
          </Stack>
        ),
      },
      ...(qualify
        ? [
            {
              field: 'propertyName',
              headerName: 'Property',
              flex: 1,
              minWidth: 160,
            } as LiftAIGridColDef<CallbackCarSummary>,
          ]
        : []),
      {
        field: 'requirement',
        headerName: 'Requirement',
        // Narrow enough to leave the totals on screen from 1440px up: the two allowances
        // sit on two lines rather than one.
        width: 120,
        sortable: false,
        description: 'What the contract allows per elevator per year, callbacks and entrapments.',
        valueGetter: (_value, row) => requirementLabel(row.targetRate, row.emergencyTargetRate),
        renderCell: ({ row }) => (
          <Stack justifyContent="center" height="100%">
            {requirementLines(row.targetRate, row.emergencyTargetRate).map((line) => (
              <Typography key={line} variant="body2" color="text.secondary" lineHeight={1.3}>
                {line}
              </Typography>
            ))}
          </Stack>
        ),
      },
      ...monthColumns,
      {
        field: 'callbacks',
        headerName: 'Callbacks',
        type: 'number',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        description: 'Regular, deferred and emergency callbacks over the selected period.',
      },
      {
        field: 'emergency',
        headerName: 'Emergency',
        type: 'number',
        width: 115,
        align: 'right',
        headerAlign: 'right',
        description: 'How many of those callbacks were entrapments.',
      },
    ];
  }, [months, qualify]);

  const onClose = useCallback(() => setOpen(null), []);

  return (
    <>
      <Box sx={MONTH_CELL_SX}>
        <LiftAITable<CallbackCarSummary>
          height={tableHeight}
          apiRef={apiRef}
          rows={summaries as CallbackCarSummary[]}
          columns={columns}
          getRowId={(row) => row.carId}
          loading={loading}
          tableHeaderTitle="Callback and Emergency Visits"
          enableQuickFilter
          quickFilterPlaceHolder="Search elevators…"
          initialState={initialState}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          paginationModel={{ pageSize: 25, page: 0 }}
        />
      </Box>
      {open ? (
        <CallbackMonthModal
          carId={open.carId}
          carName={open.carName}
          monthStart={open.monthStart}
          onClose={onClose}
        />
      ) : null}
    </>
  );
}
